/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Outlet } from 'react-router-dom';
import { type FC, useEffect, useMemo } from 'react';

import { useUpdate } from 'ahooks';
import { applyAbrI18nOverrides } from '@coze-studio/abr-i18n-overrides';
import {
  isAbrRebrandEnabled,
  patchAbrCdLocale,
  AbrBrowserTitle,
  applyAbrTheme,
} from '@coze-studio/abr-brand';
import { BrowserUpgradeWrap } from '@coze-foundation/browser-upgrade-banner';
import { resolveAppLocale, toI18nLanguage } from '@coze-arch/i18n/raw';
import { I18nProvider } from '@coze-arch/i18n/i18n-provider';
import { I18n } from '@coze-arch/i18n';
import { useUserInfo } from '@coze-arch/foundation-sdk';
import { zh_CN, en_US } from '@coze-arch/coze-design/locales';
import {
  CDLocaleProvider,
  ThemeProvider,
  enUS,
  zhCN,
} from '@coze-arch/coze-design';
import { LocaleProvider } from '@coze-arch/bot-semi';

import { GlobalLayoutComposed } from '@/components/global-layout-composed';

export const GlobalLayout: FC = () => {
  const userInfo = useUserInfo();
  const update = useUpdate();
  const currentLocale = resolveAppLocale(userInfo?.locale);
  const abrRebrand = isAbrRebrandEnabled();

  const transformedCurrentLocale = toI18nLanguage(currentLocale);
  const isOversea = currentLocale === 'en-US';
  const cdLocale = useMemo(
    () =>
      abrRebrand
        ? patchAbrCdLocale(isOversea ? en_US : zh_CN, isOversea)
        : isOversea
          ? en_US
          : zh_CN,
    [abrRebrand, isOversea],
  );

  useEffect(() => {
    applyAbrTheme();
    if (abrRebrand) {
      applyAbrI18nOverrides();
    }
  }, [abrRebrand]);

  useEffect(() => {
    if (!userInfo) {
      if (I18n.language !== 'zh-CN') {
        localStorage.setItem('i18next', 'zh-CN');
        I18n.setLang('zh-CN');
        if (abrRebrand) {
          applyAbrI18nOverrides();
        }
        update();
      }
      return;
    }

    if (I18n.language !== transformedCurrentLocale) {
      localStorage.setItem('i18next', transformedCurrentLocale);
      I18n.setLang(transformedCurrentLocale);
      if (abrRebrand) {
        applyAbrI18nOverrides();
      }
      // Force an update, otherwise the language switch will not take effect
      update();
    }
  }, [userInfo, transformedCurrentLocale, update, abrRebrand]);

  return (
    <I18nProvider i18n={I18n}>
      {abrRebrand ? <AbrBrowserTitle /> : null}
      <CDLocaleProvider locale={cdLocale}>
        <LocaleProvider locale={currentLocale === 'en-US' ? enUS : zhCN}>
          <ThemeProvider
            defaultTheme="light"
            changeSemiTheme={true}
            changeBySystem={IS_BOE}
          >
            <BrowserUpgradeWrap>
              <GlobalLayoutComposed>
                <Outlet />
              </GlobalLayoutComposed>
            </BrowserUpgradeWrap>
          </ThemeProvider>
        </LocaleProvider>
      </CDLocaleProvider>
    </I18nProvider>
  );
};
